import React, { useRef, useState } from "react";
import { Box, Popover, TextField } from "@mui/material";
import { useModel } from "hooks/useModel";
import styles from "./ChatList.module.scss";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ChatListItem } from "../../types";
import { deleteTopic, updateTopic } from "components/ai-chat/store/TopicActions";
import { useAxios } from "myAxios";

const ChatListContentItem = ({ item, index }: { item: ChatListItem; index: number }) => {
  const { selectedKey, setSelectKey, setChatlist } = useModel("aichat");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const open = Boolean(anchorEl);
  const axios = useAxios();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRename = () => {
    setIsEditing(true);
    setEditValue(item.topicName);
    handleClose();
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const setTopicName = (id: string, topicName: string) => {
    setChatlist((prev) => [
      ...prev.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            topicName,
          };
        }
        return item;
      }),
    ]);
  };

  const handleBlur = () => {
    const topicName = editValue.trim() || item.topicName;
    setIsEditing(false);
    if (topicName !== item.topicName) {
      setTopicName(item.id, topicName);
      updateTopic(axios, item.id, { ...item, topicName } as any).catch((error) => {
        setTopicName(item.id, item.topicName);
        console.error("Failed to update topic:", error);
      });
    }
  };

  const handleDelete = () => {
    handleClose();
    setChatlist((prev) =>
      prev.filter((chat) => {
        return chat.id !== item.id;
      })
    );
    if (setSelectKey && selectedKey === item.id) {
      setSelectKey(null);
    }
    deleteTopic(axios, item.id).catch(() => {
      setChatlist((prev) => [...prev, item]);
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      inputRef.current?.blur();
    } else if (e.key === "Escape") {
      setEditValue(item.topicName);
      setIsEditing(false);
    }
  };

  return (
    <Box
      className={styles.chatListItem}
      key={item.id + index}
      style={index % 2 === 0 ? { backgroundColor: "rgb(236, 236, 236, 0.4)" } : {}}
    >
      <Box
        className={selectedKey === item.id ? styles.selectedTopic : styles.topicName}
        onClick={() => !isEditing && setSelectKey(item.id)}
      >
        {isEditing ? (
          <TextField
            inputRef={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            variant="standard"
            fullWidth
            size="small"
            InputProps={{
              disableUnderline: true,
              style: { fontSize: "inherit" },
            }}
          />
        ) : (
          item.topicName
        )}
      </Box>
      <div
        onClick={handleClick}
        className={open ? styles.moreIcon : ""}
      >
        <MoreVertIcon
          htmlColor="#898989"
          fontSize="small"
        />
      </div>
      <Popover
        className={styles.chatListPopover}
        id={item.id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
        }}
      >
        <div className={styles.operateMenus}>
          <div
            className={styles.menuItem}
            onClick={handleRename}
          >
            Rename
          </div>
          <div
            className={styles.menuItem}
            onClick={handleDelete}
          >
            Delete
          </div>
        </div>
      </Popover>
    </Box>
  );
};

export default ChatListContentItem;
